import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
    GetTaskTypeStatuses,
    AddTaskTypeStatus,
    UpdateTaskTypeStatus,
    DeleteTaskTypeStatus,
    GetAllStatusBadgeColors,
} from "../../../../../../services/api/task-types-api";
import {
    Button,
    Table,
    Spinner,
} from "react-bootstrap";
import FluentUiIcon from "../../../../../../assets/fluent-ui-icon";
import StatusBadge from "./StatusBadge";
import FormStatusModal from "./FormStatusModal";

const emptyStatus = {
    Id: "",
    Name: "",
    Description: "",
    BadgeColorId: 0,
    BadgeColorName: "",
};

const FormStatuses = ({ taskTypeId, onStatusesChange }) => {
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(!!taskTypeId);
    const [saving, setSaving] = useState(false);
    const [colors, setColors] = useState([]);
    const [error, setError] = useState("");
    const [form, setForm] = useState(emptyStatus);
    const [editId, setEditId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const loadColors = useCallback(async () => {
        try {
            const res = await GetAllStatusBadgeColors();
            if (res.status === 200) setColors(res.data || []);
        } catch (err) {
            console.error("Error [GetAllStatusBadgeColors] => ", err);
        }
    }, []);

    const loadStatuses = useCallback(async () => {
        if (!taskTypeId) return;
        setLoading(true);
        setError("");
        try {
            const res = await GetTaskTypeStatuses(taskTypeId);
            if (res.status === 200) setStatuses(res.data || []);
            else setError("Failed to load statuses.");
        } catch (err) {
            console.error("Error [GetTaskTypeStatuses] => ", err);
            setError("Failed to load statuses.");
        } finally {
            setLoading(false);
        }
    }, [taskTypeId]);

    useEffect(() => {
        loadStatuses();
        loadColors();
    }, [loadStatuses, loadColors]);

    useEffect(() => {
        if (onStatusesChange && !loading) onStatusesChange(statuses);
    }, [statuses, loading, onStatusesChange]);

    const openCreate = () => {
        setForm(emptyStatus);
        setEditId(null);
        setShowModal(true);
        setError("");
        setValidationErrors({});
    };

    const openEdit = (s) => {
        setForm({
            Id: s.Id,
            Name: s.Name || "",
            Description: s.Description || "",
            BadgeColorId: s.BadgeColorId || 0,
            BadgeColorName: s.BadgeColorName || "",
        });
        setEditId(s.Id);
        setShowModal(true);
        setError("");
        setValidationErrors({});
    };

    const closeModal = () => {
        if (saving) return;
        setShowModal(false);
        setForm(emptyStatus);
        setEditId(null);
        setValidationErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
        // Clear validation error for this field when user types
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleColorPick = (colorName) => {
        const selectedColor = colors.find((c) => c.Name === colorName);
        const isDeselecting = form.BadgeColorName === colorName;

        setForm((f) => ({
            ...f,
            BadgeColorName: isDeselecting ? "" : colorName,
            BadgeColorId: isDeselecting ? 0 : (selectedColor?.Id || 0),
        }));

        // Clear badge color validation error when user selects a color
        if (!isDeselecting && validationErrors.BadgeColorName) {
            setValidationErrors((prev) => ({ ...prev, BadgeColorName: "" }));
        }
    };

    const validateForm = () => {
        const errors = {};
        const trimmedName = form.Name.trim();

        // Name validation
        if (!trimmedName) {
            errors.Name = "Name is required.";
        } else {
            // Check for duplicate name (case-insensitive)
            const isDuplicate = statuses.some(
                (s) =>
                    (s.Name || "").toLowerCase() === trimmedName.toLowerCase() &&
                    s.Id !== editId
            );
            if (isDuplicate) {
                errors.Name = `A form status with the name '${trimmedName}' already exists for this task type.`;
            }
        }

        // Badge color validation
        if (!form.BadgeColorName) {
            errors.BadgeColorName = "Badge color is required.";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const parseErrorResponse = (err, operation = "save") => {
        // Try to extract error message from various response formats
        if (err.response) {
            const { data, status } = err.response;
            
            if (status === 400) {
                // Backend validation error - return the message directly
                if (typeof data === "string") return data;
                if (data?.Message) return data.Message;
                if (data?.message) return data.message;
            }
            
            if (status === 409) {
                // Context-specific conflict messages
                if (operation === "delete") {
                    return "Status is in use and cannot be deleted.";
                }
                return "A conflict occurred. The status may have been modified by another user.";
            }
        }
        return null;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError("");

        // Client-side validation
        if (!validateForm()) {
            return;
        }

        setSaving(true);
        try {
            if (editId) {
                const res = await UpdateTaskTypeStatus(editId, {
                    Name: form.Name.trim(),
                    Description: form.Description,
                    BadgeColorId: form.BadgeColorId,
                    BadgeColorName: form.BadgeColorName,
                });
                if (res.status === 200) {
                    setStatuses((prev) => prev.map((p) => (p.Id === editId ? res.data : p)));
                    closeModal();
                } else {
                    setError("Update failed.");
                }
            } else {
                const res = await AddTaskTypeStatus(taskTypeId, {
                    Name: form.Name.trim(),
                    Description: form.Description,
                    BadgeColorId: form.BadgeColorId,
                    BadgeColorName: form.BadgeColorName,
                });
                if (res.status === 201) {
                    setStatuses((prev) => [...prev, res.data]);
                    closeModal();
                } else {
                    setError("Create failed.");
                }
            }
        } catch (err) {
            const errorMessage = parseErrorResponse(err);
            setError(errorMessage || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this status?")) return;
        try {
            const res = await DeleteTaskTypeStatus(id);
            if (res.status === 204) {
                setStatuses((prev) => prev.filter((s) => s.Id !== id));
                if (editId === id) closeModal();
            } else if (res.status === 409) {
                setError("Status is in use and cannot be deleted.");
            } else {
                setError("Delete failed.");
            }
        } catch (err) {
            console.error("Error [DeleteTaskTypeStatus] => ", err);
            const errorMessage = parseErrorResponse(err, "delete");
            setError(errorMessage || "Delete failed.");
        }
    };

    return (
        <div className="task-accordion-container task-reminders task-type-reminder">
            <div className="heading">
                <h4 className="heading-name">Form Statuses</h4>
            </div>

            {error && (
                <div className="alert alert-danger py-2 mb-3" role="alert">
                    {error}
                </div>
            )}

            {loading && (
                <div className="d-flex align-items-center gap-2">
                    <Spinner size="sm" animation="border" />
                    <span>Loading statuses...</span>
                </div>
            )}

            {!loading && statuses.length === 0 && (
                <div className="text-muted fst-italic">No statuses yet.</div>
            )}

            {!loading && statuses.length > 0 && (
                <div className="table-responsive">
                    <Table size="sm" hover className="align-middle text-left">
                        <tbody>
                            {statuses.map((s) => (
                                <tr key={s.Id} className="align-middle">
                                    <td className="align-middle">
                                        <StatusBadge status={s} colors={colors} />
                                    </td>
                                    <td className="fw-semibold align-middle">
                                        {s.Name}
                                    </td>
                                    <td className="align-middle">
                                        <div className="d-flex justify-content-center gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => openEdit(s)}
                                                aria-label="Edit"
                                            >
                                                <FluentUiIcon name="EditRegular" size={14} color="#424242" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => handleDelete(s.Id)}
                                                aria-label="Delete"
                                            >
                                                <FluentUiIcon name="DeleteRegular" size={14} color="#424242" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {!loading && (
                <div className="mt-3 text-left">
                    <Button variant="primary" onClick={openCreate}>
                        <FluentUiIcon name="AddRegular" size={14} color="#fff" />&nbsp;
                        Create New Form Status
                    </Button>
                </div>
            )}

            <FormStatusModal
                show={showModal}
                onHide={closeModal}
                onSave={handleSave}
                editId={editId}
                saving={saving}
                error={error}
                form={form}
                onChange={handleChange}
                validationErrors={validationErrors}
                colors={colors}
                onColorPick={handleColorPick}
            />
        </div>
    );
};

FormStatuses.propTypes = {
    taskTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onStatusesChange: PropTypes.func,
};

export default FormStatuses;
